import React, { useState, useEffect } from "react";
import {
  Container,
  Typography,
  Box,
  Step,
  TextField,
  Stepper,
  Stack,
  Alert,
  Chip,
  StepButton,
  Button,
  MenuItem,
  CircularProgress,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Drawer,
  Fab,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import "dayjs/locale/de";
import { ArrowBack, Visibility } from "@mui/icons-material";
import { useParams } from "react-router-dom";
import RegionImageOverlay from "../components/RegionImageOverlay.tsx";
import SnowTypeEntryList from "../components/SnowTypeEntryList.tsx";
import AvalancheTypeEntryList from "../components/AvalancheTypeEntryList.tsx";
import MarkdownEditor from "../components/MarkdownEditor.tsx";
import ImageEntryList from "../components/ImageEntryList.tsx";
import ReportPaper from "../components/ReportPaper.tsx";
import {
  AvalancheTypeEntry,
  ImageTypeEntry,
  SnowTypeEntry,
} from "../types/report.ts";
import axios from "axios";
import { loadReport } from "../utils/reportLoader.ts";
import { saveReport } from "../utils/reportSaver.ts";
import { useNavigate } from "react-router-dom";
import { processReportData } from "../utils/processReportData.ts";

export function getRegionImageUrls(regions: any, regionOptions: any): string[] {
  return regions
    .map((regionId) => {
      const region = regionOptions.find((r) => r.documentId === regionId);
      return region && region.regionImage?.url
        ? "https://backend.schnee.report" + region.regionImage.url
        : null;
    })
    .filter((url): url is string => Boolean(url));
}

function ReportCreate() {
  const { id } = useParams<{ id?: string }>();
  const navigate = useNavigate();
  const steps = [
    "Titel & Gültigkeitsdatum",
    "Regionen auswählen",
    "Schnee",
    "Lawinen",
    "Text",
    "Bilder",
    "Finalisieren",
  ];
  const [activeStep, setActiveStep] = useState(0);
  const [loadingReport, setLoadingReport] = useState(false);
  const [loadingError, setLoadingError] = useState("");

  // Back navigation dialog
  const [showBackDialog, setShowBackDialog] = useState(false);

  // Saving state
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState("");

  // Preview state
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewData, setPreviewData] = useState<any>("");

  // Step 1: Titel & Gültigkeitsdatum
  const [title, setTitle] = useState("");
  const [validFrom, setValidFrom] = useState(null);
  const [validUntil, setValidUntil] = useState(null);

  // Step 2: Regionen
  const [regions, setRegions] = useState<string[]>([]); // selected region documentIds
  const [regionOptions, setRegionOptions] = useState<any[]>([]); // fetched region objects
  const [loadingRegions, setLoadingRegions] = useState(false);
  const [errorRegions, setErrorRegions] = useState("");

  // Step 3: Snow entries
  const [snowEntries, setSnowEntries] = useState<SnowTypeEntry[]>([]);

  // Step 4: Avalanche entries
  const [avaEntries, setAvaEntries] = useState<AvalancheTypeEntry[]>([]);
  const [avalancheSituation, setAvalancheSituation] = useState("");

  // Step 5: Text
  const [markdownText, setMarkdownText] = useState<string>(`
  ### Schneemenge
  
  Beschreibe hier die aktuelle Schneemenge und vergleiche sie z.B. mit Durchschnittswerten.
  
  ### Schneequalität
  
  Erläutere die Beschaffenheit des Schnees (z. B. Pulverschnee, Nassschnee, Harsch).
  
  ### Schneegrenze
  
  Gib an, ab welcher Höhe Schnee liegt.
  
  ### Schneeverteilung
  
  Aktuelle Verteilung.
  
  ### Empfehlungen
  
  Empfehlungen für Skitouren basierend auf der Schneelage.
  
  ### Fazit
  
  Zusammenfassung der wichtigsten Punkte und eine Einschätzung der aktuellen Schneeverhältnisse.
  `);

  // Step 6: Bilder
  const [imageEntries, setImageEntries] = useState<ImageTypeEntry[]>([]);
  const [hadToUpload, setHadToUpload] = useState<any>(0);

  // Load existing report if ID is provided
  useEffect(() => {
    if (id) {
      setLoadingReport(true);
      loadReport(id)
        .then((reportData) => {
          if (reportData) {
            // Update all state variables with the loaded data
            setTitle(reportData.title);
            setValidFrom(dayjs(reportData.validFrom));
            setValidUntil(dayjs(reportData.validUntil));
            setRegions(reportData.regions);
            setSnowEntries(reportData.snowEntries);
            setAvaEntries(reportData.avaEntries);
            setAvalancheSituation(reportData.avalancheSituation);
            setMarkdownText(reportData.markdownText);
            setImageEntries(reportData.imageEntries);
          } else {
            setLoadingError("Report nicht gefunden");
          }
        })
        .catch((error) => {
          console.error("Error loading report:", error);
          setLoadingError("Fehler beim Laden des Reports");
        })
        .finally(() => setLoadingReport(false));
    }
  }, [id, hadToUpload]);

  // Load region options
  useEffect(() => {
    if (regionOptions.length === 0 && !loadingRegions) {
      setLoadingRegions(true);
      axios
        .get("https://backend.schnee.report/api/regions?populate=regionImage")
        .then((res) => {
          setRegionOptions(res.data.data || []);
        })
        .catch(() => setErrorRegions("Fehler beim Laden der Regionen"))
        .finally(() => setLoadingRegions(false));
    }
    // eslint-disable-next-line
  }, []);

  // Stepper Navigation
  const handleNext = () => setActiveStep((prev) => prev + 1);
  const handleBack = () => setActiveStep((prev) => prev - 1);

  // Back navigation handlers
  const handleBackClick = () => {
    setShowBackDialog(true);
  };

  const handleBackConfirm = () => {
    setShowBackDialog(false);
    navigate("/dashboard");
  };

  const handleBackCancel = () => {
    setShowBackDialog(false);
  };

  // Save handlers
  const handleSaveDraft = async () => {
    setSaving(true);
    setSaveError("");

    const result = await saveReport(
      {
        title,
        validFrom,
        validUntil,
        regions,
        snowEntries,
        avaEntries,
        avalancheSituation,
        markdownText,
        imageEntries,
      },
      false, // Don't publish
      id
    );

    setSaving(false);

    if (result.success) {
      // Navigate to dashboard or show success message
      navigate("/dashboard");
    } else {
      setSaveError(result.error || "Fehler beim Speichern");
    }
  };

  // Preview handler
  const handlePreview = async () => {
    setSaving(true);
    setSaveError("");

    const result = await saveReport(
      {
        title,
        validFrom,
        validUntil,
        regions,
        snowEntries,
        avaEntries,
        avalancheSituation,
        markdownText,
        imageEntries,
      },
      false, // Don't publish
      id
    );
    setSaving(false);

    if (result.success && result.documentId) {
      // If no id exists yet, navigate to the edit route with the new documentId
      if (!id) {
        navigate(`/report/create/${result.documentId}`, { replace: true });
      }
      if (result.hadToUpload) {
        setHadToUpload(hadToUpload + 1);
      }

      // post proc.
      const updatedReport = processReportData(result.resp);
      setPreviewData(updatedReport);
      setPreviewOpen(true);
    } else {
      setSaveError(result.error || "Fehler beim Speichern für Vorschau");
    }
  };

  const handlePublish = async () => {
    setSaving(true);
    setSaveError("");

    const result = await saveReport(
      {
        title,
        validFrom,
        validUntil,
        regions,
        snowEntries,
        avaEntries,
        avalancheSituation,
        markdownText,
        imageEntries,
      },
      true, // Publish
      id
    );

    setSaving(false);

    if (result.success) {
      // Navigate to dashboard or show success message
      navigate("/dashboard");
    } else {
      setSaveError(result.error || "Fehler beim Veröffentlichen");
    }
  };

  // Step Content
  function getStepContent(step: number) {
    switch (step) {
      case 0:
        return (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 3,
              mt: 4,
              maxWidth: 400,
              mx: "auto", // horizontal zentrieren
              alignItems: "center", // Inhalte im Inneren mittig (optional)
            }}
          >
            <TextField
              label="Titel"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              required
              fullWidth
            />
            <DatePicker
              label="Gültig ab"
              value={validFrom}
              onChange={(newValue) => setValidFrom(newValue)}
              slotProps={{
                textField: {
                  fullWidth: true,
                  required: true,
                },
              }}
            />
            <DatePicker
              label="Gültig bis (optional)"
              value={validUntil}
              onChange={(newValue) => setValidUntil(newValue)}
              slotProps={{
                textField: {
                  fullWidth: true,
                  required: false,
                },
              }}
            />
          </Box>
        );
      case 1:
        // Bildquellen für ausgewählte Regionen
        const selectedRegionImages = regions
          .map((regionId) => {
            const region = regionOptions.find((r) => r.documentId === regionId);
            return region && region.regionImage && region.regionImage.url
              ? "https://backend.schnee.report" + region.regionImage.url
              : null;
          })
          .filter(Boolean) as string[];
        return (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 3,
              mt: 4,
              maxWidth: 400,
              mx: "auto", // horizontal zentrieren
              alignItems: "center", // Inhalte im Inneren mittig (optional)
            }}
          >
            {errorRegions && <Alert severity="error">{errorRegions}</Alert>}
            <TextField
              select
              label="Regionen"
              value={regions}
              onChange={(e) =>
                setRegions(
                  typeof e.target.value === "string"
                    ? e.target.value.split(",")
                    : e.target.value
                )
              }
              SelectProps={{
                multiple: true,
                renderValue: (selected) =>
                  (selected as string[])
                    .map((id) => {
                      const r = regionOptions.find(
                        (opt) => opt.documentId === id
                      );
                      return r ? r.name : id;
                    })
                    .join(", "),
              }}
              fullWidth
              helperText={
                loadingRegions ? "Lade Regionen..." : "Mehrfachauswahl möglich"
              }
              disabled={loadingRegions}
            >
              {regionOptions.map((region) => (
                <MenuItem key={region.documentId} value={region.documentId}>
                  {region.name}
                </MenuItem>
              ))}
            </TextField>
            {/* Additiv überlagerte Bilder der ausgewählten Regionen als Canvas */}
            {selectedRegionImages.length > 0 && (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  width: "100%",
                }}
              >
                <RegionImageOverlay imageSources={selectedRegionImages} />
              </Box>
            )}
            {/* Bildnamen als Chips mittig */}
            <Stack
              direction="row"
              spacing={1}
              justifyContent="center"
              sx={{
                width: "100%",
                flexWrap: "wrap",
                rowGap: 1,
              }}
            >
              {regions.map((regionId) => {
                const region = regionOptions.find(
                  (r) => r.documentId === regionId
                );
                return region ? (
                  <Chip
                    key={regionId}
                    label={region.name}
                    color="primary"
                    variant="outlined"
                  />
                ) : null;
              })}
            </Stack>
          </Box>
        );
      case 2:
        return (
          <SnowTypeEntryList
            entries={snowEntries}
            setEntries={setSnowEntries}
          />
        );
      case 3:
        return (
          <AvalancheTypeEntryList
            entries={avaEntries}
            setEntries={setAvaEntries}
            avalancheSituation={avalancheSituation}
            setAvalancheSituation={setAvalancheSituation}
          />
        );
      case 4:
        return (
          <MarkdownEditor
            value={markdownText}
            onChange={(value) => setMarkdownText(value || "")}
          />
        );
      case 5:
        return (
          <ImageEntryList entries={imageEntries} setEntries={setImageEntries} />
        );
      case 6:
        return (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 3,
              mt: 4,
              maxWidth: 400,
              mx: "auto",
              alignItems: "center",
            }}
          >
            <Typography variant="h6" gutterBottom>
              Bericht finalisieren
            </Typography>
            {saveError && (
              <Alert severity="error" sx={{ width: "100%" }}>
                {saveError}
              </Alert>
            )}
            <Box
              sx={{
                display: "flex",
                gap: 2,
                justifyContent: "center",
                width: "100%",
              }}
            >
              <Button
                variant="outlined"
                fullWidth
                onClick={handleSaveDraft}
                disabled={saving}
                sx={{
                  color: "#667eea",
                  borderColor: "#667eea",
                  padding: "12px 24px",
                  borderRadius: "8px",
                  fontWeight: 600,
                  transition: "all 0.3s ease",
                  "&:hover": {
                    borderColor: "#667eea",
                    backgroundColor: "rgba(102, 126, 234, 0.1)",
                    transform: "translateY(-2px)",
                  },
                }}
              >
                {saving ? (
                  <CircularProgress size={24} sx={{ color: "#667eea" }} />
                ) : (
                  "Als Entwurf speichern"
                )}
              </Button>
              <Button
                variant="contained"
                fullWidth
                onClick={handlePublish}
                disabled={saving}
                sx={{
                  background:
                    "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                  color: "white",
                  padding: "12px 24px",
                  borderRadius: "8px",
                  fontWeight: 600,
                  transition: "all 0.3s ease",
                  "&:hover": {
                    transform: "translateY(-2px)",
                    boxShadow: "0 8px 16px rgba(102, 126, 234, 0.4)",
                  },
                }}
              >
                {saving ? (
                  <CircularProgress size={24} sx={{ color: "white" }} />
                ) : (
                  "Freigeben"
                )}
              </Button>
            </Box>
          </Box>
        );
      default:
        return <div>Platzhalter für Schritt {step + 1}</div>;
    }
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="de">
      <Box
        sx={{
          minHeight: "100vh",
          background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
          color: "white",
          py: 4,
        }}
      >
        <Container maxWidth="sm">
          <Box sx={{ display: "flex", alignItems: "center", mb: 4 }}>
            <IconButton
              onClick={handleBackClick}
              sx={{
                color: "white",
                mr: 2,
                "&:hover": { backgroundColor: "rgba(255, 255, 255, 0.1)" },
              }}
            >
              <ArrowBack />
            </IconButton>
            <Typography variant="h5" component="h1" sx={{ color: "white" }}>
              {id ? "Schneereport bearbeiten" : "Neuer Schneereport"}
            </Typography>
          </Box>

          {loadingReport && (
            <Box
              display="flex"
              justifyContent="center"
              alignItems="center"
              my={4}
            >
              <CircularProgress sx={{ color: "white" }} />
            </Box>
          )}

          {loadingError && (
            <Alert severity="error" sx={{ mb: 4 }}>
              {loadingError}
            </Alert>
          )}
          <Stepper
            nonLinear
            activeStep={activeStep}
            alternativeLabel
            sx={{ mb: 4 }}
          >
            {steps.map((label, index) =>
              index <= activeStep + 2 && index >= activeStep - 1 ? (
                <Step key={label} index={index}>
                  <StepButton onClick={() => setActiveStep(index)}>
                    {label}
                  </StepButton>
                </Step>
              ) : null
            )}
          </Stepper>
          <Box
            sx={{
              background: "white",
              borderRadius: "16px",
              boxShadow: "0 20px 60px rgba(0, 0, 0, 0.3)",
              p: 4,
              color: "#333",
              minHeight: 220,
            }}
          >
            {getStepContent(activeStep)}
          </Box>
          <Box sx={{ display: "flex", justifyContent: "space-between", mt: 4 }}>
            <Button
              disabled={activeStep === 0}
              onClick={handleBack}
              variant="outlined"
              sx={{
                color: "white",
                borderColor: "white",
                padding: "12px 32px",
                borderRadius: "8px",
                fontWeight: 600,
                transition: "all 0.3s ease",
                "&:hover": {
                  borderColor: "white",
                  backgroundColor: "rgba(255, 255, 255, 0.1)",
                  transform: "translateY(-2px)",
                },
                "&:disabled": {
                  borderColor: "rgba(255, 255, 255, 0.3)",
                  color: "rgba(255, 255, 255, 0.3)",
                },
              }}
            >
              Zurück
            </Button>
            <Button
              onClick={handleNext}
              disabled={activeStep < 0 || activeStep >= 6}
              variant="contained"
              sx={{
                background: "white",
                color: "#667eea",
                padding: "12px 32px",
                borderRadius: "8px",
                fontWeight: 600,
                transition: "all 0.3s ease",
                "&:hover": {
                  background: "rgba(255, 255, 255, 0.9)",
                  transform: "translateY(-2px)",
                  boxShadow: "0 8px 16px rgba(255, 255, 255, 0.3)",
                },
                "&:disabled": {
                  background: "rgba(255, 255, 255, 0.3)",
                  color: "rgba(102, 126, 234, 0.5)",
                },
              }}
            >
              Weiter
            </Button>
          </Box>
        </Container>

        {/* Floating preview button */}
        <Fab
          color="primary"
          aria-label="preview"
          onClick={handlePreview}
          disabled={saving}
          sx={{
            position: "fixed",
            right: "10px",
            top: "5%",
            transform: "translateY(-50%)",
            background: "white",
            color: "#667eea",
            transition: "all 0.3s ease",
            "&:hover": {
              background: "rgba(255, 255, 255, 0.9)",
              transform: "translateY(-50%) translateY(-2px)",
              boxShadow: "0 8px 16px rgba(255, 255, 255, 0.3)",
            },
          }}
        >
          {saving ? (
            <CircularProgress size={24} sx={{ color: "#667eea" }} />
          ) : (
            <Visibility />
          )}
        </Fab>

        {/* Preview drawer */}
        <Drawer
          anchor="bottom"
          open={previewOpen}
          onClose={() => setPreviewOpen(false)}
          PaperProps={{
            sx: { maxWidth: "900px", marginLeft: "auto", marginRight: "auto" },
          }}
        >
          <Box>
            <Box
              sx={{
                alignItems: "center",
                justifyContent: "center",
                display: "flex",
                marginTop: "10px",
              }}
            >
              <Button onClick={() => setPreviewOpen(false)} variant="outlined">
                Schließen
              </Button>
            </Box>
            <Box
              sx={{
                display: "flex",
                flex: 1,
                overflow: "hidden",
                justifyContent: "center",
              }}
            >
              {previewData && (
                <ReportPaper
                  entry={previewData}
                  regions={regions}
                  regionOptions={regionOptions}
                  selectedRegionImages={getRegionImageUrls(
                    regions,
                    regionOptions
                  )}
                />
              )}
            </Box>
          </Box>
        </Drawer>

        {/* Back confirmation dialog */}
        <Dialog
          open={showBackDialog}
          onClose={handleBackCancel}
          aria-labelledby="back-dialog-title"
          aria-describedby="back-dialog-description"
        >
          <DialogTitle id="back-dialog-title">
            Ohne Speichern zurück?
          </DialogTitle>
          <DialogContent>
            <DialogContentText id="back-dialog-description">
              Möchten Sie wirklich zum Dashboard zurückkehren, ohne den Bericht
              zu speichern? Alle nicht gespeicherten Änderungen gehen verloren.
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleBackCancel} color="primary">
              Nein
            </Button>
            <Button onClick={handleBackConfirm} color="primary" autoFocus>
              Ja
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </LocalizationProvider>
  );
}

export default ReportCreate;
